import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Campaign, SubmissionData, Notification, Brand, SiteConfig, User, Aggregator, CommunityPost } from '../../types';
import { AdminView, UserRole, SubmissionType } from '../../enums';
import { LayoutDashboard, Megaphone, FileText, BarChart3, LogOut, Menu, PieChart, Table as TableIcon, AlertTriangle, Settings, Users, UserCircle, ChevronDown, ListTree, Lightbulb, MessageCircle, ThumbsUp, ThumbsDown, LayoutTemplate } from 'lucide-react';
import { DashboardView } from './views/DashboardView';
import { CampaignsView } from './views/CampaignsView';
import { SubmissionsView } from './views/SubmissionsView';
import { ProposalsView } from './views/ProposalsView';
import { ReportsView } from './views/ReportsView';
import { AnalyticsView } from './views/AnalyticsView';
import { TableView } from './views/TableView';
import { SettingsView } from './views/SettingsView';
import { UserManagementView } from './views/UserManagementView';
import { MenuBuilder } from '../../components/menu-builder/MenuBuilder';
import { ProfileView } from './views/ProfileView';
import { NotificationPanel } from './NotificationPanel';
import { TraceView } from './views/TraceView';
import { Dashboard } from '../Dashboard';
import { ManagerDashboardView } from './views/ManagerDashboardView';
import { ManagersDashboardView } from './views/ManagersDashboardView';
import { CommunityView } from './views/CommunityView';
import { apiClient } from '../../services/apiClient';
import { hasPerm } from '../../utils/permissions';
import { pathToView, viewToPath } from '../../utils/viewPaths';

interface AdminPortalProps {
  onLogout: () => void;
  onViewPublicSite: () => void; // Added handler for public view
  campaigns: Campaign[];
  onUpdateCampaigns: (campaigns: Campaign[]) => void;
  submissions: SubmissionData[];
  onUpdateSubmissions: (submissions: SubmissionData[]) => void;
  brands: Brand[];
  onUpdateBrands: (brands: Brand[]) => void;
  aggregators: Aggregator[];
  onUpdateAggregators: (aggs: Aggregator[]) => void;
  siteConfig: SiteConfig; 
  onUpdateSiteConfig: (config: SiteConfig) => void; 
  notifications: Notification[];
  onMarkAllRead: () => void;
  onClearAll: () => void;
  onAddNotification: (type: 'info' | 'success' | 'warning' | 'error', title: string, message: string, targetView?: AdminView, targetId?: string) => void;
  showToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  users: User[];
  onUpdateUsers: (users: User[]) => void;
  currentUser: User;
  onUpdateUser: (user: User) => void; 
  // Manager views props
  onNewSubmission?: () => void;
  onNewProposal?: () => void;
  onEditSubmission?: (submission: SubmissionData) => void;
}

export const AdminPortal: React.FC<AdminPortalProps> = ({ 
  onLogout, 
  onViewPublicSite,
  campaigns, 
  onUpdateCampaigns, 
  submissions, 
  onUpdateSubmissions,
  brands,
  onUpdateBrands,
  aggregators,
  onUpdateAggregators,
  siteConfig,
  onUpdateSiteConfig,
  notifications,
  onMarkAllRead,
  onClearAll,
  onAddNotification,
  showToast,
  users,
  onUpdateUsers,
  currentUser,
  onUpdateUser,
  onNewSubmission,
  onNewProposal,
  onEditSubmission
}) => {
  const currencyCode = siteConfig.currencyCode || 'QAR';
  const isAdminUser = currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SEO;
  const [managerDashboardTarget, setManagerDashboardTarget] = useState<{ id: string; name: string } | null>(
    currentUser.role === UserRole.MANAGER ? { id: currentUser.id, name: currentUser.name } : null
  );
  const visibleNotifications = useMemo(() => {
    if (currentUser.role !== UserRole.MANAGER) return notifications;
    const allowed = new Set<AdminView>([
      AdminView.MANAGER_DASHBOARD,
      AdminView.MANAGER_DASHBOARD,
      AdminView.SUBMISSIONS,
      AdminView.PROPOSALS,
    ]);
    return notifications.filter((n) => !n.targetView || allowed.has(n.targetView));
  }, [notifications, currentUser.role]);

  const hasPermission = useCallback(
    (view: AdminView) => {
      if (view === AdminView.PROFILE) return true;
      return hasPerm(currentUser, view);
    },
    [currentUser]
  );

  const canUseManagerDashboard = hasPermission(AdminView.MANAGER_DASHBOARD);
  const canUseManagerDashboardView = useCallback(
    (target?: { id: string; name: string } | null) => {
      const resolvedTarget = target ?? managerDashboardTarget;
      if (!canUseManagerDashboard) return false;
      return canUseManagerDashboard || (isAdminUser && !!resolvedTarget);
    },
    [canUseManagerDashboard, isAdminUser, managerDashboardTarget]
  );

  const getBrandNamesFromIds = useCallback(
    (ids: string[] | undefined | null) => {
      if (!ids || !ids.length) return [];
      const lookup = new Map(brands.map((b) => [b.id, b.name]));
      return ids.map((id) => lookup.get(id)).filter((n): n is string => Boolean(n));
    },
    [brands]
  );

  const filterSubmissionsByBrands = useCallback(
    (source: SubmissionData[], brandIds: string[] | undefined | null) => {
      if (!brandIds || brandIds.length === 0) return source;
      const allowedNames = getBrandNamesFromIds(brandIds).map((n) => n.toLowerCase());
      if (!allowedNames.length) return [];
      const allowedSet = new Set(allowedNames);
      return source.filter((s) => {
        if (!s.brands || s.brands.length === 0) return true;
        return s.brands.some((b) => allowedSet.has(String(b).toLowerCase()));
      });
    },
    [getBrandNamesFromIds]
  );

  const getUserBrandIds = useCallback(
    (userId: string | undefined | null) => {
      if (!userId) return [];
      const found = users.find((u) => u.id === userId);
      return found?.brandIds || [];
    },
    [users]
  );

  const submissionsForViews = useMemo(
    () => {
      const targetBrands = currentUser.role === UserRole.MANAGER ? currentUser.brandIds || [] : [];
      return filterSubmissionsByBrands(submissions, targetBrands);
    },
    [currentUser.brandIds, currentUser.role, submissions, filterSubmissionsByBrands]
  );

  const getDefaultView = useCallback((): AdminView => {
    if (currentUser.role === UserRole.MANAGER) {
      if (canUseManagerDashboard) return AdminView.MANAGER_DASHBOARD;
    }
    if (hasPermission(AdminView.DASHBOARD)) return AdminView.DASHBOARD;
    return AdminView.PROFILE;
  }, [canUseManagerDashboard, hasPermission, currentUser.role]);

  // MUST be declared before any useEffect that references it (TDZ fix)
  const canAccessView = useCallback(
    (view: AdminView) => {
      if (view === AdminView.PROFILE) return true;
      if (view === AdminView.MANAGER_DASHBOARD) return canUseManagerDashboard;
      return hasPermission(view);
    },
    [canUseManagerDashboard, hasPermission]
  );

  const [currentView, setCurrentView] = useState<AdminView>(() => getDefaultView());
  const [previousView, setPreviousView] = useState<AdminView | null>(null);
  const [managerDashboardInitialTab, setManagerDashboardInitialTab] = useState<'overview' | 'history'>('overview');
  const [showMySubmissions, setShowMySubmissions] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

  // Refs to hold latest versions of callbacks for popstate handler
  const canAccessViewRef = useRef(canAccessView);
  const getDefaultViewRef = useRef(getDefaultView);
  useEffect(() => { canAccessViewRef.current = canAccessView; }, [canAccessView]);
  useEffect(() => { getDefaultViewRef.current = getDefaultView; }, [getDefaultView]);

  // Helper to resolve view from current URL - uses refs to avoid stale closures
  const resolveViewFromLocation = useCallback((): AdminView => {
    const fromPath = pathToView(window.location.pathname);
    if (fromPath && canAccessViewRef.current(fromPath)) {
      return fromPath;
    }
    return getDefaultViewRef.current();
  }, []);

  // Single navigation function - ONLY for user-initiated navigation
  const navigateTo = useCallback((view: AdminView) => {
    if (view === currentView) return;
    if (!canAccessView(view)) {
      view = getDefaultView();
    }
    const path = viewToPath(view);
    if (typeof window !== 'undefined' && window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    setCurrentView(view);
  }, [currentView, canAccessView, getDefaultView]);

  // Initial load + popstate handler - NO pushState calls here
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Sync on mount - resolve view from URL
    const initialView = resolveViewFromLocation();
    const initialPath = viewToPath(initialView);
    // If URL doesn't match, fix it once
    if (window.location.pathname !== initialPath) {
      window.history.replaceState({}, '', initialPath);
    }
    setCurrentView(initialView);
    
    // Handle back/forward - ONLY update React state, NO pushState
    const handlePopstate = () => {
      const resolvedView = resolveViewFromLocation();
      const path = viewToPath(resolvedView);
      setCurrentView(resolvedView);
      if (window.location.pathname !== path) {
        window.history.replaceState({}, '', path);
      }
    };
    
    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, [resolveViewFromLocation]);

  const [hasNewCommunityActivity, setHasNewCommunityActivity] = useState(false);
  const [communityHeadKey, setCommunityHeadKey] = useState<string | null>(null);
  const [latestCommunityPost, setLatestCommunityPost] = useState<CommunityPost | null>(null);
  
  // State to handle deep linking from notifications
  const [focusSubmissionId, setFocusSubmissionId] = useState<string | null>(null);
  const [managerScopedSubmissions, setManagerScopedSubmissions] = useState<SubmissionData[]>(
    currentUser.role === UserRole.MANAGER ? submissions : []
  );
  const [managerViewLoading, setManagerViewLoading] = useState(false);
  
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentUser.role === UserRole.MANAGER) {
      setManagerDashboardTarget({ id: currentUser.id, name: currentUser.name });
    } else if (!isAdminUser) {
      setManagerDashboardTarget(null);
    }
  }, [currentUser.id, currentUser.name, currentUser.role, isAdminUser]);

  useEffect(() => {
    if (!canAccessView(currentView)) {
      setCurrentView(getDefaultView());
    }
  }, [canAccessView, currentView, getDefaultView]);

  useEffect(() => {
    if (currentUser.role === UserRole.MANAGER) {
      setManagerScopedSubmissions(submissions);
    }
  }, [submissions, currentUser.role]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const checkCommunityHead = async () => {
      try {
        const res = await apiClient.community.listPosts({ limit: 1 });
        if (cancelled) return;
        const head = res.items?.[0];
        if (head) {
          setLatestCommunityPost(head);
        }
        const nextKey = head
          ? `${head.id}:${head.updatedAt}:${head.commentCount}:${head.reactionCounts?.like ?? 0}:${head.reactionCounts?.dislike ?? 0}`
          : 'none';
        setCommunityHeadKey((prev) => {
          if (prev && prev !== nextKey) {
            setHasNewCommunityActivity(true);
          }
          return nextKey;
        });
      } catch {
        // ignore background poll errors
      }
    };

    if (currentView !== AdminView.COMMUNITY) {
      checkCommunityHead();
      timer = setInterval(checkCommunityHead, 15000);
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [currentView]);

  const loadManagerSubmissions = useCallback(
    async (managerId: string) => {
      if (!managerId || !isAdminUser) return;
      setManagerViewLoading(true);
      try {
        const scoped = await apiClient.submissions.list({ managerId });
        const items = Array.isArray((scoped as any)?.items)
          ? (scoped as any).items
          : Array.isArray(scoped)
          ? scoped
          : [];
        setManagerScopedSubmissions(items);
      } catch (error) {
        console.warn('Failed to load manager-scoped submissions', error);
        setManagerScopedSubmissions(
          submissions.filter(
            (s) => s.managerId === managerId || s.manager?.fileNumber === managerId
          )
        );
      } finally {
        setManagerViewLoading(false);
      }
    },
    [isAdminUser, submissions]
  );

  useEffect(() => {
    if (managerDashboardTarget && isAdminUser) {
      setManagerScopedSubmissions([]);
      loadManagerSubmissions(managerDashboardTarget.id);
    } else if (!managerDashboardTarget && isAdminUser) {
      setManagerScopedSubmissions([]);
    }
  }, [managerDashboardTarget, isAdminUser, loadManagerSubmissions]);

  const resolveManagerSubmissions = useCallback(() => {
    let base: SubmissionData[] = [];
    let targetBrandIds: string[] = [];
    if (currentUser.role === UserRole.MANAGER) {
      base = managerScopedSubmissions.length ? managerScopedSubmissions : submissions;
      targetBrandIds = currentUser.brandIds || [];
    } else if (managerDashboardTarget) {
      base = managerScopedSubmissions;
      targetBrandIds = getUserBrandIds(managerDashboardTarget.id);
    } else {
      base = managerScopedSubmissions.length ? managerScopedSubmissions : submissions;
    }
    return filterSubmissionsByBrands(base, targetBrandIds);
  }, [currentUser.role, currentUser.brandIds, managerScopedSubmissions, submissions, managerDashboardTarget, filterSubmissionsByBrands, getUserBrandIds]);

  // Handle Profile Click Outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
            setIsProfileDropdownOpen(false);
        }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNotificationClick = (notification: Notification) => {
      if (currentUser.role === UserRole.MANAGER && notification.targetId) {
        setFocusSubmissionId(notification.targetId);
        navigateTo(AdminView.MANAGER_DASHBOARD);
        return;
      }
      if (notification.targetView) {
          navigateTo(notification.targetView);
          
          // If targeting a submission or proposal, set the ID so the view can open it
          if ((notification.targetView === AdminView.SUBMISSIONS || notification.targetView === AdminView.PROPOSALS) && notification.targetId) {
              setFocusSubmissionId(notification.targetId);
          }
      }
  };

  const handleViewManagerDashboard = (manager: User) => {
      const target = { id: manager.id, name: manager.name };
      if (!canUseManagerDashboardView(target)) {
        showToast('error', 'Access Denied', 'You do not have permission to view manager dashboards.');
        return;
      }
      setManagerDashboardTarget(target);
      setManagerDashboardInitialTab('overview');
      if (isAdminUser) {
        loadManagerSubmissions(target.id);
      }
      navigateTo(AdminView.MANAGER_DASHBOARD);
      setMobileMenuOpen(false);
  };

  const handleExitManagerView = () => {
      const fallbackTarget = currentUser.role === UserRole.MANAGER ? { id: currentUser.id, name: currentUser.name } : null;
      setManagerDashboardTarget(fallbackTarget);
      setManagerDashboardInitialTab('overview');
      if (currentUser.role === UserRole.MANAGER) {
        setManagerScopedSubmissions(submissions);
      } else {
        setManagerScopedSubmissions([]);
      }
      navigateTo(getDefaultView());
  };

  const handleOpenSubmissionDetail = (submissionId: string, type?: SubmissionType) => {
    setPreviousView(currentView);
    if (currentView === AdminView.MANAGER_DASHBOARD) {
      setManagerDashboardInitialTab('history');
    }
    setFocusSubmissionId(submissionId);
    if (currentUser.role === UserRole.MANAGER) {
      navigateTo(AdminView.MANAGER_DASHBOARD);
    } else {
      navigateTo(type === SubmissionType.PROPOSAL ? AdminView.PROPOSALS : AdminView.SUBMISSIONS);
    }
    setMobileMenuOpen(false);
  };

  const renderContent = () => {
    if (!canAccessView(currentView)) {
        const fallback = getDefaultView();
        if (fallback === AdminView.MANAGER_DASHBOARD && canUseManagerDashboard) {
            return (
              <ManagerDashboardView
                  submissions={resolveManagerSubmissions()}
                  managerId={(managerDashboardTarget || { id: currentUser.id }).id}
                  managerName={(managerDashboardTarget || { name: currentUser.name }).name}
                  isAdminView={isAdminUser && (managerDashboardTarget ? managerDashboardTarget.id !== currentUser.id : false)}
                  adminViewerName={isAdminUser ? currentUser.name : undefined}
                  canCreate={hasPerm(currentUser, AdminView.SUBMIT_SUBMISSIONS) || hasPerm(currentUser, AdminView.SUBMIT_PROPOSALS)}
                  onNewSubmission={hasPerm(currentUser, AdminView.SUBMIT_SUBMISSIONS) ? onNewSubmission : undefined}
                  onNewProposal={hasPerm(currentUser, AdminView.SUBMIT_PROPOSALS) ? onNewProposal : undefined}
                  onOpenMySubmissions={() => setShowMySubmissions(true)}
                  onExitAdminView={isAdminUser ? handleExitManagerView : undefined}
                  loading={managerViewLoading}
                  onOpenSubmissionDetail={handleOpenSubmissionDetail}
                  canViewHistory={true}
                  initialTab={managerDashboardInitialTab}
                  onHistoryNavigate={() => setManagerDashboardInitialTab('history')}
              />
            );
        }
        return <DashboardView submissions={submissionsForViews} campaigns={campaigns} onViewSite={onViewPublicSite} currentUser={currentUser} onNavigate={navigateTo} brands={brands} />;
    }

    switch (currentView) {
      case AdminView.DASHBOARD: 
        return <DashboardView submissions={submissionsForViews} campaigns={campaigns} onViewSite={onViewPublicSite} currentUser={currentUser} onNavigate={navigateTo} brands={brands} />;
      case AdminView.MANAGER_DASHBOARD:
        // If showMySubmissions is true, render the Dashboard (My Submissions) view
        if (showMySubmissions) {
          return (
            <Dashboard
              submissions={resolveManagerSubmissions()}
              onNew={onNewSubmission || (() => {})}
              onHome={onLogout}
              onEdit={onEditSubmission || (() => {})}
              managerName={currentUser.name}
              managerId={currentUser.id}
              canSubmit={hasPerm(currentUser, AdminView.SUBMIT_SUBMISSIONS)}
              onNewProposal={hasPerm(currentUser, AdminView.SUBMIT_PROPOSALS) ? onNewProposal : undefined}
              focusSubmissionId={focusSubmissionId}
              onBack={() => {
                setShowMySubmissions(false);
                setFocusSubmissionId(null);
              }}
            />
          );
        }
        // Otherwise render the ManagerDashboardView
        return (
          <ManagerDashboardView
            submissions={resolveManagerSubmissions()}
            managerId={(managerDashboardTarget || { id: currentUser.id }).id}
            managerName={(managerDashboardTarget || { name: currentUser.name }).name}
            isAdminView={isAdminUser && (managerDashboardTarget ? managerDashboardTarget.id !== currentUser.id : false)}
            adminViewerName={isAdminUser ? currentUser.name : undefined}
            canCreate={hasPerm(currentUser, AdminView.SUBMIT_SUBMISSIONS) || hasPerm(currentUser, AdminView.SUBMIT_PROPOSALS)}
            onNewSubmission={hasPerm(currentUser, AdminView.SUBMIT_SUBMISSIONS) ? onNewSubmission : undefined}
            onNewProposal={hasPerm(currentUser, AdminView.SUBMIT_PROPOSALS) ? onNewProposal : undefined}
            onOpenMySubmissions={() => setShowMySubmissions(true)}
            onExitAdminView={isAdminUser ? handleExitManagerView : undefined}
            loading={managerViewLoading}
            onOpenSubmissionDetail={handleOpenSubmissionDetail}
            canViewHistory={true}
            initialTab={managerDashboardInitialTab}
            onHistoryNavigate={() => setManagerDashboardInitialTab('history')}
          />
        );
      case AdminView.CAMPAIGNS: return <CampaignsView campaigns={campaigns} onUpdateCampaigns={onUpdateCampaigns} onAddNotification={onAddNotification} showToast={showToast} aggregators={aggregators} currencyCode={currencyCode} brands={brands} currentUser={currentUser} />;
      case AdminView.SUBMISSIONS: 
        return <SubmissionsView 
                  submissions={currentUser.role === UserRole.MANAGER ? submissionsForViews : submissions} 
                  brands={brands} 
                  campaigns={campaigns}
                  onUpdateSubmissions={onUpdateSubmissions} 
                  onAddNotification={onAddNotification} 
                  showToast={showToast}
                  currentUser={currentUser} 
                  users={users} 
                  focusSubmissionId={focusSubmissionId}
                  onNavigate={navigateTo}
                  currencyCode={currencyCode}
                  onBack={() => {
                    if (previousView && canAccessView(previousView)) {
                      navigateTo(previousView);
                    } else {
                      navigateTo(getDefaultView());
                    }
                    setPreviousView(null);
                    setFocusSubmissionId(null);
                  }}
                />;
      case AdminView.PROPOSALS: 
        return <ProposalsView 
                  submissions={currentUser.role === UserRole.MANAGER ? submissionsForViews : submissions} 
                  brands={brands} 
                  campaigns={campaigns}
                  onUpdateSubmissions={onUpdateSubmissions} 
                  onAddNotification={onAddNotification} 
                  showToast={showToast}
                  currentUser={currentUser} 
                  users={users} 
                  focusSubmissionId={focusSubmissionId}
                  onNavigate={navigateTo}
                  currencyCode={currencyCode}
                  onBack={() => {
                    if (previousView && canAccessView(previousView)) {
                      navigateTo(previousView);
                    } else {
                      navigateTo(getDefaultView());
                    }
                    setPreviousView(null);
                    setFocusSubmissionId(null);
                  }}
                />; 
      case AdminView.TABLE: return <TableView submissions={submissionsForViews} campaigns={campaigns} currencyCode={currencyCode} />;
      case AdminView.REPORTS: return <ReportsView submissions={submissionsForViews} campaigns={campaigns} onUpdateSubmissions={onUpdateSubmissions} onAddNotification={onAddNotification} showToast={showToast} aggregators={aggregators} brands={brands} />;
      case AdminView.ANALYTICS: return <AnalyticsView submissions={submissionsForViews} aggregators={aggregators} currencyCode={currencyCode} brands={brands} />;
      case AdminView.MENU_BUILDER: return <MenuBuilder currentUser={currentUser} />;
      case AdminView.COMMUNITY: return <CommunityView currentUser={currentUser} hasNewCommunityActivity={hasNewCommunityActivity} onCommunityActivityFlagChange={setHasNewCommunityActivity} />;
      case AdminView.SETTINGS: return <SettingsView brands={brands} onUpdateBrands={onUpdateBrands} aggregators={aggregators} onUpdateAggregators={onUpdateAggregators} siteConfig={siteConfig} onUpdateSiteConfig={onUpdateSiteConfig} showToast={showToast} />;
      case AdminView.USERS: return <UserManagementView users={users} brands={brands} onUpdateUsers={onUpdateUsers} currentUser={currentUser} showToast={showToast} onViewManagerDashboard={handleViewManagerDashboard} />;
      case AdminView.MANAGERS_DASHBOARD: return <ManagersDashboardView users={users} brands={brands} submissions={submissionsForViews} campaigns={campaigns} currentUser={currentUser} onOpenSubmissionDetail={handleOpenSubmissionDetail} />;
      case AdminView.PROFILE: return <ProfileView currentUser={currentUser} onUpdateUser={onUpdateUser} showToast={showToast} submissions={submissionsForViews} brands={brands} />;
      case AdminView.TRACE: return <TraceView currentUser={currentUser} />;
      default: return <DashboardView submissions={submissionsForViews} campaigns={campaigns} onViewSite={onViewPublicSite} currentUser={currentUser} onNavigate={navigateTo} brands={brands} />;
    }
  };

  const getPageTitle = (view: AdminView) => {
    switch(view) {
        case AdminView.DASHBOARD: return 'Dashboard Overview';
        case AdminView.MANAGER_DASHBOARD: return 'Manager Dashboard';
        case AdminView.MANAGERS_DASHBOARD: return 'Managers Dashboard';
        case AdminView.CAMPAIGNS: return 'Campaign Management';
        case AdminView.SUBMISSIONS: return 'Submission Review';
        case AdminView.PROPOSALS: return 'Proposals Review';
        case AdminView.TABLE: return 'Master Data Table';
        case AdminView.REPORTS: return 'Reports & Exports';
        case AdminView.ANALYTICS: return 'Data Analytics';
        case AdminView.MENU_BUILDER: return 'Menu Builder';
        case AdminView.COMMUNITY: return 'Community Updates';
        case AdminView.SETTINGS: return 'System Settings';
        case AdminView.USERS: return 'User Management';
        case AdminView.PROFILE: return 'My Profile';
        case AdminView.TRACE: return 'Trace / Track';
        default: return 'Admin Portal';
    }
  };

  const portalLabel = currentUser.role === UserRole.MANAGER ? 'Manager Portal' : 'Admin Portal';

  const NavItem = ({ view, icon: Icon, label, variant, showBubble, latestPost }: { view: AdminView, icon: any, label: string, variant?: 'community', showBubble?: boolean, latestPost?: CommunityPost | null }) => {
    const isActive = currentView === view;
    const [showTooltip, setShowTooltip] = useState(false);
    // Hide item if user lacks permission
    if (!canAccessView(view)) return null;

    const base = variant === 'community'
      ? {
          active: 'bg-amber-400 text-zinc-900 shadow-lg shadow-amber-200',
          inactive: 'text-amber-500 border border-amber-200 bg-amber-50 hover:bg-amber-100',
          icon: isActive ? 'text-zinc-900' : 'text-amber-600'
        }
      : {
          active: 'bg-teal-600 text-white shadow-lg shadow-teal-900/20',
          inactive: 'text-zinc-400 hover:bg-zinc-800 hover:text-white',
          icon: isActive ? 'text-white' : 'text-zinc-500 group-hover:text-white'
        };

    return (
        <button
            onClick={() => {
                navigateTo(view);
                if (view === AdminView.COMMUNITY) {
                  setHasNewCommunityActivity(false);
                  setCommunityHeadKey(null);
                }
                setMobileMenuOpen(false);
            }}
            className={`relative w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all font-bold text-sm mb-1
                ${isActive ? base.active : base.inactive}
            `}
        >
            <Icon className={`w-5 h-5 ${base.icon}`} />
            {label}
            {showBubble && (
              <div 
                className="absolute right-3 top-2.5"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <span
                  className="px-2 py-0.5 rounded-full bg-teal-500 text-white text-[9px] font-extrabold uppercase tracking-[0.12em] animate-pulse shadow-sm shadow-teal-200 cursor-help"
                  aria-label="New community updates"
                >
                  New
                </span>
                
                {showTooltip && latestPost && (
                  <div className="absolute bottom-full -right-3 mb-2 w-60 bg-white rounded-xl shadow-xl border border-zinc-200 p-3 z-50 animate-in fade-in zoom-in-95 duration-200">
                    <div className="flex items-center justify-between mb-2 pb-2 border-b border-zinc-100">
                      <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider">Latest Update</span>
                      <span className="text-[10px] text-zinc-400">{new Date(latestPost.createdAt || '').toLocaleDateString()}</span>
                    </div>
                    <h4 className="text-xs font-bold text-zinc-900 mb-2 line-clamp-2 text-left">{latestPost.title}</h4>
                    <div className="flex items-center gap-3 text-zinc-500">
                      <div className="flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" />
                        <span className="text-[10px] font-bold">{latestPost.reactionCounts?.like || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ThumbsDown className="w-3 h-3" />
                        <span className="text-[10px] font-bold">{latestPost.reactionCounts?.dislike || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        <span className="text-[10px] font-bold">{latestPost.commentCount || 0}</span>
                      </div>
                    </div>
                    <div className="absolute bottom-[-4px] right-5 w-2 h-2 bg-white border-b border-r border-zinc-200 rotate-45"></div>
                  </div>
                )}
              </div>
            )}
        </button>
    )
  }

  return (
    <div className="flex h-screen bg-zinc-50/50 overflow-hidden font-sans text-zinc-900">
      
      {/* Logout Confirmation Modal */}
      {isLogoutModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-zinc-900/50 backdrop-blur-sm animate-fade-in">
              <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center border border-zinc-100">
                  <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-2">Sign Out?</h3>
                  <p className="text-zinc-500 text-sm mb-8 leading-relaxed">
                      Are you sure you want to log out of the admin portal? Any unsaved changes may be lost.
                  </p>
                  <div className="flex gap-3">
                      <button 
                          onClick={() => setIsLogoutModalOpen(false)}
                          className="flex-1 px-6 py-3 bg-white border border-zinc-200 text-zinc-700 font-bold rounded-xl hover:bg-zinc-50 transition-colors"
                      >
                          Cancel
                      </button>
                      <button 
                          onClick={onLogout}
                          className="flex-1 px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 shadow-lg shadow-red-200 transition-all active:scale-95"
                      >
                          Logout
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 bg-zinc-900 border-r border-zinc-800 flex-col h-full z-40 relative">
          <div className="p-6 border-b border-zinc-800 h-20 flex items-center">
              <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-zinc-900 font-bold text-lg shadow-md shadow-white/10 overflow-hidden">
                    {siteConfig.logo ? (
                        <img src={siteConfig.logo} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                        siteConfig.appName.charAt(0)
                    )}
                  </div>
                  <div>
                      <h1 className="text-sm font-extrabold text-white tracking-tight">{portalLabel}</h1>
                      <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider block truncate max-w-[120px]">{siteConfig.appName}</span>
                  </div>
              </div>
          </div>
          
          <div className="p-4 flex-1 overflow-y-auto custom-scrollbar">
              <div className="mb-2 px-4 text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Main Menu</div>
              <NavItem view={AdminView.DASHBOARD} icon={LayoutDashboard} label="Dashboard" />
              <NavItem view={AdminView.MANAGER_DASHBOARD} icon={BarChart3} label="Manager Dashboard" />
              
              <NavItem view={AdminView.CAMPAIGNS} icon={Megaphone} label="Campaigns" />
              <NavItem view={AdminView.TABLE} icon={TableIcon} label="Master Table" />
              <NavItem view={AdminView.REPORTS} icon={BarChart3} label="Reports & Exports" />
              <NavItem view={AdminView.ANALYTICS} icon={PieChart} label="Analytics" />
              <NavItem view={AdminView.MENU_BUILDER} icon={LayoutTemplate} label="Menu Builder" />
              <NavItem view={AdminView.SUBMISSIONS} icon={FileText} label="Submissions" />
              <NavItem view={AdminView.PROPOSALS} icon={Lightbulb} label="Proposals" />
              <NavItem view={AdminView.TRACE} icon={ListTree} label="Trace / Track" />
              
              <div className="mt-6 mb-2 px-4 text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Configuration</div>
              
              <NavItem view={AdminView.USERS} icon={Users} label="User Management" />
              <NavItem view={AdminView.MANAGERS_DASHBOARD} icon={LayoutDashboard} label="Managers Dashboard" />
              <NavItem view={AdminView.SETTINGS} icon={Settings} label="System Settings" />
          </div>

          <div className="p-4 border-t border-zinc-800">
              <NavItem view={AdminView.COMMUNITY} icon={MessageCircle} label="Community" variant="community" showBubble={hasNewCommunityActivity} latestPost={latestCommunityPost} />
              <button 
                onClick={() => setIsLogoutModalOpen(true)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:bg-red-900/20 hover:text-red-400 font-bold text-sm transition-colors"
              >
                  <LogOut className="w-5 h-5" />
                  Logout
              </button>
          </div>
      </aside>

      {/* Main Content Layout */}
      <div className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          {/* Top Header */}
          <header className="bg-white/80 backdrop-blur-md border-b border-zinc-200 h-20 px-6 md:px-10 flex items-center justify-between shrink-0 z-30">
               <div className="flex items-center gap-4">
                   {/* Mobile Menu Toggle */}
                   <button 
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden p-2 rounded-xl text-zinc-500 hover:bg-zinc-100"
                   >
                        <Menu className="w-6 h-6" />
                   </button>

                   <h2 className="text-xl font-bold text-zinc-900 tracking-tight">
                        {showMySubmissions && currentView === AdminView.MANAGER_DASHBOARD ? 'My Submissions' : getPageTitle(currentView)}
                   </h2>
               </div>

               <div className="flex items-center gap-4 md:gap-6">
                    <NotificationPanel 
                        notifications={visibleNotifications} 
                        onMarkAllRead={onMarkAllRead} 
                        onClearAll={onClearAll}
                        onNotificationClick={handleNotificationClick}
                    />
                    
                    {/* Profile Dropdown */}
                    <div className="relative pl-4 border-l border-zinc-200" ref={profileDropdownRef}>
                        <button 
                            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                            className="flex items-center gap-3 focus:outline-none"
                        >
                            <div className="text-right hidden sm:block">
                                <div className="text-sm font-bold text-zinc-900 leading-none">{currentUser.name}</div>
                                <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wide mt-1">{currentUser.department}</div>
                            </div>
                            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold border-2 border-white shadow-md shadow-teal-200 cursor-pointer hover:scale-105 transition-transform overflow-hidden">
                                {currentUser.profileImage ? (
                                    <img src={currentUser.profileImage} alt={currentUser.name} className="w-full h-full object-cover" />
                                ) : (
                                    currentUser.name.charAt(0)
                                )}
                            </div>
                            <ChevronDown className="w-4 h-4 text-zinc-400" />
                        </button>

                        {isProfileDropdownOpen && (
                            <div className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-xl shadow-zinc-200/50 border border-zinc-100 z-50 overflow-hidden animate-fade-in-up origin-top-right">
                                <div className="px-4 py-3 border-b border-zinc-100">
                                    <p className="text-sm font-bold text-zinc-900">{currentUser.name}</p>
                                    <p className="text-xs text-zinc-500 truncate">{currentUser.email}</p>
                                </div>
                                <div className="p-2">
                                    <button 
                                        onClick={() => { navigateTo(AdminView.PROFILE); setIsProfileDropdownOpen(false); }}
                                        className="w-full text-left px-4 py-2 text-sm font-bold text-zinc-600 hover:bg-zinc-50 hover:text-teal-600 rounded-xl flex items-center gap-2"
                                    >
                                        <UserCircle className="w-4 h-4" /> My Profile
                                    </button>
                                    <button 
                                        onClick={() => { navigateTo(AdminView.DASHBOARD); setIsProfileDropdownOpen(false); }}
                                        className="w-full text-left px-4 py-2 text-sm font-bold text-zinc-600 hover:bg-zinc-50 hover:text-teal-600 rounded-xl flex items-center gap-2"
                                    >
                                        <LayoutDashboard className="w-4 h-4" /> Dashboard
                                    </button>
                                    <hr className="my-1 border-zinc-100" />
                                    <button 
                                        onClick={() => { setIsProfileDropdownOpen(false); setIsLogoutModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 text-sm font-bold text-red-500 hover:bg-red-50 rounded-xl flex items-center gap-2"
                                    >
                                        <LogOut className="w-4 h-4" /> Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
               </div>
          </header>

          {/* Mobile Navigation Dropdown (Visible only on small screens when open) */}
          {mobileMenuOpen && (
              <div className="md:hidden absolute top-20 left-0 right-0 bg-white border-b border-zinc-200 z-20 shadow-xl animate-fade-in-down">
                  <div className="p-4 space-y-2">
                      <NavItem view={AdminView.DASHBOARD} icon={LayoutDashboard} label="Dashboard" />
                      <NavItem view={AdminView.MANAGER_DASHBOARD} icon={BarChart3} label="Manager Dashboard" />
                      
                      <NavItem view={AdminView.CAMPAIGNS} icon={Megaphone} label="Campaigns" />
                      <NavItem view={AdminView.TABLE} icon={TableIcon} label="Master Table" />
                      <NavItem view={AdminView.REPORTS} icon={BarChart3} label="Reports & Exports" />
                      <NavItem view={AdminView.ANALYTICS} icon={PieChart} label="Analytics" />
                      <NavItem view={AdminView.MENU_BUILDER} icon={LayoutTemplate} label="Menu Builder" />
                      <NavItem view={AdminView.USERS} icon={Users} label="Users" />
                      <NavItem view={AdminView.MANAGERS_DASHBOARD} icon={LayoutDashboard} label="Managers Dashboard" />
                      <NavItem view={AdminView.TRACE} icon={ListTree} label="Trace / Track" />
                      <NavItem view={AdminView.SETTINGS} icon={Settings} label="Settings" />
                      
                      <NavItem view={AdminView.SUBMISSIONS} icon={FileText} label="Submissions" />
                      <NavItem view={AdminView.PROPOSALS} icon={Lightbulb} label="Proposals" />
                      <NavItem view={AdminView.PROFILE} icon={UserCircle} label="Profile" />
                      <NavItem view={AdminView.COMMUNITY} icon={MessageCircle} label="Community" variant="community" showBubble={hasNewCommunityActivity} latestPost={latestCommunityPost} />
                      <hr className="border-zinc-100 my-2"/>
                      <button 
                        onClick={() => {
                            setMobileMenuOpen(false);
                            setIsLogoutModalOpen(true);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-500 hover:bg-red-50 hover:text-red-600 font-bold text-sm transition-colors"
                      >
                          <LogOut className="w-5 h-5" />
                          Logout
                      </button>
                  </div>
              </div>
          )}

          {/* Scrollable View Area */}
          <main className={`flex-1 ${currentView === AdminView.MENU_BUILDER ? 'overflow-hidden flex flex-col p-4 md:p-10' : 'overflow-y-auto p-4 md:p-10 scroll-smooth'}`}>
              <div className={`transition-all duration-300 ${
                  currentView === AdminView.MENU_BUILDER ? 'w-full h-full' :
                  (currentView === AdminView.SUBMISSIONS || currentView === AdminView.PROPOSALS || currentView === AdminView.TABLE || currentView === AdminView.REPORTS || currentView === AdminView.SETTINGS || currentView === AdminView.ANALYTICS || currentView === AdminView.MANAGERS_DASHBOARD || currentView === AdminView.DASHBOARD
                  ? 'w-full pb-20' 
                  : 'max-w-6xl mx-auto pb-20')
              }`}>
                 {renderContent()}
              </div>
          </main>
      </div>
    </div>
  );
};
